
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CreditCard, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/SupabaseAuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface PaymentsSettingsTabProps {
  onSaveSettings?: (section: string) => void;
}

const PaymentsSettingsTab: React.FC<PaymentsSettingsTabProps> = ({ onSaveSettings }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("config");

  const handleSavePaymentSettings = () => {
    toast({
      title: "Settings Saved",
      description: "Payment settings have been updated successfully"
    });
    
    if (onSaveSettings) {
      onSaveSettings('Payment');
    }
  };

  return (
    <div className="space-y-8">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="config">
            <Settings className="mr-2 h-4 w-4" />
            Payment Configuration
          </TabsTrigger>
          <TabsTrigger value="gateways">
            <CreditCard className="mr-2 h-4 w-4" />
            Payment Gateways
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="config" className="space-y-6 pt-6">
          <Card>
            <CardHeader>
              <CardTitle>Payment Processing Configuration</CardTitle>
              <CardDescription>
                Configure how payments are processed in the system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-lg font-medium">Default Currency</h3>
                <select 
                  className="w-full border border-input bg-background px-3 py-2 text-sm ring-offset-background rounded-md"
                  defaultValue="USD"
                >
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="GBP">British Pound (GBP)</option>
                  <option value="CAD">Canadian Dollar (CAD)</option>
                  <option value="AUD">Australian Dollar (AUD)</option>
                </select>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-medium">Payment Processing Fees</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="processing-fee">Processing Fee (%)</Label>
                    <Input 
                      id="processing-fee" 
                      type="number" 
                      defaultValue="2.9" 
                      step="0.1" 
                      min="0" 
                      max="100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fixed-fee">Fixed Fee ($)</Label>
                    <Input 
                      id="fixed-fee" 
                      type="number" 
                      defaultValue="0.30" 
                      step="0.01" 
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch id="pass-fees" defaultChecked />
                <Label htmlFor="pass-fees">
                  Pass fees to customers
                </Label>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSavePaymentSettings}>Save Configuration</Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="gateways" className="space-y-6 pt-6">
          <Card>
            <CardHeader>
              <CardTitle>Payment Gateways</CardTitle>
              <CardDescription>
                Configure payment processing integrations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-medium">Primary Payment Gateway</h3>
                <p className="text-muted-foreground mb-4">
                  Configure your primary payment gateway for processing transactions
                </p>
                
                <div className="flex items-center p-4 border rounded-md">
                  <div className="mr-4">
                    <CreditCard className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-medium">Stripe</h4>
                    <p className="text-sm text-muted-foreground">Connected</p>
                  </div>
                  <Button>Configure</Button>
                </div>
              </div>
              
              <Button onClick={() => onSaveSettings && onSaveSettings('Payment')}>
                Save Gateway Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentsSettingsTab;
